import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

const DEFAULT_ADMIN_EMAILS = [
  'hailemariameyayu@gmail.com',
  'hailemariameyayu2012@gmail.com'
];

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allowedEmailsList, setAllowedEmailsList] = useState([]);

  // Check email permissions against the backend database
  const fetchPermissions = useCallback(async (email) => {
    if (!email) return null;
    const isDefaultAdmin = DEFAULT_ADMIN_EMAILS.includes(email.toLowerCase().trim());
    try {
      const apiURL = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${apiURL}/admin-emails?email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        return {
          allowed: data.allowed || isDefaultAdmin,
          role: data.role || (isDefaultAdmin ? 'admin' : 'guest'),
          isAdmin: data.isAdmin || isDefaultAdmin,
          canEdit: data.canEdit !== undefined ? data.canEdit : isDefaultAdmin,
          canUpdate: data.canUpdate !== undefined ? data.canUpdate : isDefaultAdmin,
          canInsert: data.canInsert !== undefined ? data.canInsert : isDefaultAdmin,
          canLike: data.canLike !== undefined ? data.canLike : true,
        };
      }
    } catch (err) {
      console.warn('Failed to fetch permissions from DB, using fallback:', err.message);
    }
    // Fallback if offline
    return {
      allowed: isDefaultAdmin,
      role: isDefaultAdmin ? 'admin' : 'guest',
      isAdmin: isDefaultAdmin,
      canEdit: isDefaultAdmin,
      canUpdate: isDefaultAdmin,
      canInsert: isDefaultAdmin,
      canLike: true,
    };
  }, []);

  // Fetch all allowed emails (for admin management)
  const refreshAllowedEmails = useCallback(async () => {
    try {
      const apiURL = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${apiURL}/admin-emails`);
      if (res.ok) {
        const data = await res.json();
        setAllowedEmailsList(data.emails || []);
        return data.emails || [];
      }
    } catch (err) {
      console.warn('Failed to load allowed emails list:', err.message);
    }
    return [];
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const permissions = await fetchPermissions(firebaseUser.email);
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          isAdmin: permissions?.isAdmin || false,
          canEdit: permissions?.canEdit || false,
          canUpdate: permissions?.canUpdate || false,
          canInsert: permissions?.canInsert || false,
          canLike: permissions?.canLike !== undefined ? permissions.canLike : true,
          role: permissions?.role || 'guest',
          isAllowed: permissions?.allowed || false
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [fetchPermissions]);

  // Methods to manage allowed emails
  const addAllowedEmail = async (emailData) => {
    const apiURL = process.env.REACT_APP_API_URL || '/api';
    const res = await fetch(`${apiURL}/admin-emails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-email': user?.email || ''
      },
      body: JSON.stringify({
        ...emailData,
        addedBy: user?.email || 'admin'
      })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to add allowed email');
    }
    await refreshAllowedEmails();
    return res.json();
  };

  const updateAllowedEmail = async (id, updateData) => {
    const apiURL = process.env.REACT_APP_API_URL || '/api';
    const res = await fetch(`${apiURL}/admin-emails?id=${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-user-email': user?.email || ''
      },
      body: JSON.stringify(updateData)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to update email permissions');
    }
    await refreshAllowedEmails();
    return res.json();
  };

  const deleteAllowedEmail = async (id) => {
    const apiURL = process.env.REACT_APP_API_URL || '/api';
    const res = await fetch(`${apiURL}/admin-emails?id=${id}`, {
      method: 'DELETE',
      headers: {
        'x-user-email': user?.email || ''
      }
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to delete allowed email');
    }
    await refreshAllowedEmails();
    return res.json();
  };

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked. Please allow popups for this site.');
      } else if (err.code === 'auth/operation-not-allowed') {
        throw new Error('Google sign-in is not enabled. Please contact admin.');
      } else if (err.code === 'auth/unauthorized-domain') {
        throw new Error('This domain is not authorized. Please contact admin.');
      }
      throw err;
    }
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signInWithGoogle,
      logout,
      allowedEmailsList,
      refreshAllowedEmails,
      addAllowedEmail,
      updateAllowedEmail,
      deleteAllowedEmail
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
